// Help & support modal (Blueprint R-018) — reachable from the sidebar footer
// (visible in simple mode) and the top bar. Contents: the support chain
// (hidden while SUPPORT_WA is empty — v20.1 parity), the app version, the
// CURRENT realtime connection state in the Live chip's own vocabulary, and
// "Copy recent error refs" — the founder's-WhatsApp support loop: a nurse
// copies the TU-… refs and pastes them into a message; the admin greps the
// ring buffer viewer for the details.

import { store, SLICES } from '../state/store.js';
import { esc } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { register } from '../ui/actions.js';
import { openModal, closeModal } from '../ui/modal.js';
import { readErrorLog } from '../errors.js';
import { LIVE_CHIP_COPY } from '../realtime.js';

// Support WhatsApp number, e.g. '+254 7…'. EMPTY until the founder decides
// the number to publish — while empty the whole support-chain block is
// hidden rather than showing a dead contact (v20.1 parity).
export const SUPPORT_WA = '';

/**
 * Pure: one line per logged error, newest LAST (paste reads chronologically).
 * Refs only plus timestamp + source — the PII-free subset a nurse can safely
 * paste into WhatsApp.
 * @param {any[]} entries readErrorLog() output (oldest first)
 * @returns {string} '' when there is nothing to copy
 */
export function formatErrorRefs(entries) {
  if (!Array.isArray(entries)) return '';
  return entries
    .filter((e) => e && e.ref)
    .map((e) => [e.ref, e.at, e.source].filter(Boolean).join(' · '))
    .join('\n');
}

/**
 * Pure: the modal body HTML.
 * @param {{version: string, status?: string, supportWa?: string}} opts
 *   status: REALTIME slice status ('live'|'connecting'|'offline')
 * @returns {string}
 */
export function helpBodyHTML(opts) {
  const conn =
    LIVE_CHIP_COPY[/** @type {'live'|'connecting'|'offline'} */ (opts.status)] ||
    LIVE_CHIP_COPY.connecting;
  const wa = opts.supportWa || '';
  return (
    (wa
      ? '<div class="help-row help-support">If something is wrong, WhatsApp the support line: ' +
        `<b>${esc(wa)}</b></div>`
      : '') +
    `<div class="help-row">Connection: <b>${esc(conn.text)}</b></div>` +
    `<div class="help-row">App version: <b>${esc(opts.version)}</b></div>` +
    '<div class="help-row help-hint">If you were given an error reference (TU-…), ' +
    'use the button below to copy your recent references and send them to support.</div>'
  );
}

/**
 * Copy text to the clipboard: async Clipboard API first, hidden-textarea
 * execCommand fallback (older WebViews on clinic phones). Resolves true on
 * success. Never throws.
 * @param {string} text
 * @param {any} [doc] test injection
 * @returns {Promise<boolean>}
 */
export async function copyText(text, doc) {
  const d = doc || /** @type {any} */ (globalThis).document;
  const nav = /** @type {any} */ (globalThis).navigator;
  try {
    if (nav && nav.clipboard && nav.clipboard.writeText) {
      await nav.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the textarea path */
  }
  try {
    const ta = d.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.className = 'sr-copy';
    d.body.appendChild(ta);
    ta.select();
    const okCopy = d.execCommand('copy');
    ta.remove();
    return !!okCopy;
  } catch {
    return false;
  }
}

async function copyErrorRefs() {
  const text = formatErrorRefs(readErrorLog());
  if (!text) {
    toast('No recent error references on this device', 'ok');
    return;
  }
  const done = await copyText(text);
  if (done) toast('Error references copied — paste them to support', 'ok');
  else toast('Could not copy — please note the reference from the error message', 'err');
}

/** Open the Help modal. @returns {void} */
export function openHelp() {
  const rt = store.get(SLICES.REALTIME) || {};
  openModal(
    'Help & support',
    'Tumaini Patient Support Programme',
    helpBodyHTML({ version: APP_VERSION, status: rt.status, supportWa: SUPPORT_WA }),
    [
      { l: 'Copy recent error refs', k: 'copy', fn: copyErrorRefs },
      { l: 'Close', v: 'pri', fn: () => closeModal() },
    ]
  );
}

let _wired = false;

/**
 * Mount the Help entry points into the shell's slots (top bar icon + sidebar
 * footer button — the latter keeps Help one tap away in simple mode) and
 * register the actions. Call once after mountShell().
 * @returns {void}
 */
export function initHelp() {
  const doc = /** @type {any} */ (globalThis).document;
  if (!doc) return;
  const top = doc.getElementById('help-slot');
  if (top) {
    top.innerHTML =
      '<button class="help-btn" data-action="help-open" title="Help and support" aria-label="Help and support">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
      'stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/>' +
      '<path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></button>';
  }
  const side = doc.getElementById('sb-help-slot');
  if (side) {
    side.innerHTML =
      '<button class="mode-chip sb-help" data-action="help-open">Help &amp; support</button>';
  }
  if (!_wired) {
    _wired = true;
    register('help-open', openHelp);
  }
}
